import { Fragment, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import * as Api from "@/api";
import { HELP_BACKSTAGE } from "@/constants/HelpUrls";
import ContentLoader from "@/design/components/ContentLoader";
import FabBar from "@/design/components/FabBar";
import Page from "@/design/layouts/Page";
import { setSystemSettingsPage } from "@/store/reducers/app";
import { disableSearch, enableSearch, setHelpUrl } from "@/store/reducers/ui";
import { signOut } from "@/utils/firebase";
import useSearch from "@/utils/hooks/useSearch";
import { toggleLoadingState } from "@/utils/utility";
import Valkyrie, { viCity, viGear, viPlus } from "@sippy-platform/valkyrie";

import { Button, Card, Grid, Pagination, Stack, Typography, useMediaQuery, useTheme } from "@mui/material";
import { useHotkey } from "@tanstack/react-hotkeys";

import AddPbx from "../pbxes/AddPbx";
import PbxCard from "./PbxCard";
import PbxCardHighlighted from "./PbxCardHighlighted";

export default function Backstage() {
  const dispatch = useDispatch();
  const theme = useTheme();

  const auth = useSelector((state) => state.auth);
  const pbxStore = useSelector((state) => state.backstage.pbxes);
  const settings = useSelector((state) => state.auth.settings);
  const recentPbxes = useSelector((state) => state.settings.recentPbxes);
  const searchValue = useSelector((state) => state.ui.searchValue);
  const progressBar = useSelector((state) => state.ui.progressBar);

  const [manageDialog, setManageDialog] = useState(false);

  const matchesXlUp = useMediaQuery(theme.breakpoints.up("xl"));
  const matchesLgUp = useMediaQuery(theme.breakpoints.up("lg"));
  const matchesMdUp = useMediaQuery(theme.breakpoints.up("md"));
  const matchesSmUp = useMediaQuery(theme.breakpoints.up("sm"));

  useHotkey("Alt+N", () => setManageDialog(true));

  useEffect(() => signOut(), []);

  useEffect(() => {
    toggleLoadingState(true);
    Promise.resolve(Api.getPbxes()).finally(toggleLoadingState);
  }, [auth.accessLevel]);

  useEffect(() => {
    dispatch(setHelpUrl(HELP_BACKSTAGE));
  }, [dispatch]);

  // PBX Cards
  const recents = useMemo(() => {
    if (!!settings.backstageRecents && pbxStore.meta.count && recentPbxes.length >= 1) {
      return recentPbxes
        .slice(0, matchesXlUp ? 12 : matchesLgUp ? 8 : matchesMdUp ? 9 : matchesSmUp ? 6 : 4)
        .filter((recentPbx) => pbxStore.list.find((pbx) => pbx.context === recentPbx.context));
    }

    return [];
  }, [
    settings.backstageRecents,
    pbxStore.meta.count,
    pbxStore.list,
    recentPbxes,
    matchesXlUp,
    matchesLgUp,
    matchesMdUp,
    matchesSmUp,
  ]);

  // Create pbx dialog
  async function closeDialog() {
    setManageDialog(false);
    await Promise.resolve(Api.getPbxes());
  }

  // Search every time pbxList or searchQuery changes
  const searchResults = useSearch(pbxStore.list, searchValue, ["name", "context"]);

  const [page, setPage] = useState(0);

  const handleChangePage = (event, page) => {
    setPage(page - 1);
  };

  const getPbxList = useMemo(() => {
    if (searchValue) {
      return searchResults;
    }

    if (settings.backstageSort === "context") {
      return searchResults
        .slice(0)
        .sort((a, b) => (a.context > b.context ? 1 : b.context > a.context ? -1 : 0))
        .slice(page * 100, (page + 1) * 100);
    }

    if (settings.backstageSort === "name") {
      return searchResults.slice(page * 100, (page + 1) * 100).reduce((groups, pbx) => {
        const groupChar = pbx.name.charAt(0).toUpperCase();
        const pbxGroup = groupChar.match(/^[A-Z]+$/i) ? groupChar : groupChar.match(/^[0-9]+$/i) ? "#" : "&";

        if (!groups[pbxGroup]) {
          groups[pbxGroup] = [];
        }

        // Grouping
        groups[pbxGroup].push(pbx);

        return groups;
      }, {});
    }

    return searchResults;
  }, [page, searchResults, searchValue, settings.backstageSort]);

  useEffect(() => {
    dispatch(enableSearch());
  }, [dispatch]);
  useEffect(() => () => dispatch(disableSearch()), [dispatch]);

  return (
    <Page fabBar maxWidth="xxl">
      <title>{`Backstage \u00b7 ${import.meta.env.SIPPY_APP_TITLE}`}</title>
      {auth.accessLevel <= 1 && manageDialog && <AddPbx open={manageDialog} closeDialog={closeDialog} />}
      <Grid container spacing={1} id="pbxes-list">
        {recents.length >= 1 && ((matchesSmUp && searchValue) || !searchValue) && (
          <>
            <Grid size={12}>
              <Stack
                direction="row"
                sx={{
                  alignItems: "center",
                  height: 40,
                }}
              >
                <Typography variant="h5" component="h3">
                  Recently used
                </Typography>
              </Stack>
            </Grid>
            {recents.map((recent) => (
              <Grid key={recent.id} size={{ xs: 12, sm: 6, md: 4, lg: 3, xl: 2 }}>
                <PbxCardHighlighted pbx={recent} />
              </Grid>
            ))}
          </>
        )}
        <Grid style={{ display: "flex", alignItems: "center" }} size={12}>
          <Stack
            direction="row"
            sx={{
              alignItems: "center",
              height: 40,
            }}
          >
            <Typography variant="h5" component="h3">
              PBXes
            </Typography>
          </Stack>
        </Grid>
        {pbxStore.meta.count && Object.keys(getPbxList).length >= 1 ? (
          <>
            {settings.backstageSort === "name" && !searchValue
              ? Object.keys(getPbxList).map((key) => (
                  <Fragment key={key}>
                    <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3, xl: 2 }}>
                      <Card
                        id={key}
                        sx={{
                          background: "transparent",
                          height: 56,
                          width: 64,
                          display: "flex",
                          flexDirection: "row",
                          alignItems: "center",
                          justifyItems: "center",
                          padding: "4px 16px",
                        }}
                        elevation={0}
                      >
                        <Typography variant="h3" style={{ margin: 0, textAlign: "center", flexGrow: 1 }}>
                          {key}
                        </Typography>
                      </Card>
                    </Grid>
                    {getPbxList[key].map((pbx) => (
                      <Grid key={pbx.id} size={{ xs: 12, sm: 6, md: 4, lg: 3, xl: 2 }}>
                        <PbxCard pbx={pbx} />
                      </Grid>
                    ))}
                  </Fragment>
                ))
              : getPbxList.map((pbx) => (
                  <Grid key={pbx.id} size={{ xs: 12, sm: 6, md: 4, lg: 3, xl: 2 }}>
                    <PbxCard pbx={pbx} />
                  </Grid>
                ))}
          </>
        ) : (
          <ContentLoader
            count={pbxStore.meta.count}
            loading={progressBar}
            noItems="You don't have any PBXes yet"
            noResults="No PBXes in your current query"
            fetching="Getting your PBXes"
            icon={viCity}
          />
        )}
      </Grid>
      <FabBar>
        {Math.ceil(searchResults.length / 100) > 1 && (
          <Pagination count={Math.ceil(searchResults.length / 100)} page={page + 1} onChange={handleChangePage} />
        )}
        <Button
          variant="contained"
          onClick={() => dispatch(setSystemSettingsPage("backstage"))}
          sx={{ px: 1.25, fontSize: "1rem" }}
        >
          <Valkyrie icon={viGear} />
        </Button>
        {auth.accessLevel <= 1 && (
          <Button
            variant="contained"
            id="addPbx"
            onClick={() => setManageDialog(true)}
            startIcon={<Valkyrie icon={viPlus} />}
          >
            Add
          </Button>
        )}
      </FabBar>
    </Page>
  );
}
